import { useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { usePortalDocuments, usePortalDownloadDocument } from '@/hooks/usePortalDocuments';
import type { PortalDocumentItem } from '@/hooks/usePortalDocuments';
import { PortalPageHead } from '@/components/portal/PortalChrome';
import { PortalIcon } from '@/components/portal/PortalIcon';
import { formatPortalFileSize, formatPortalShortDate } from '@/lib/portalFormat';

export function PortalDocuments() {
  const { data: documents = [], isLoading, error } = usePortalDocuments();
  const downloadMutation = usePortalDownloadDocument();
  const [downloadingId, setDownloadingId] = useState<string | null>(null);

  const handleDownload = async (doc: PortalDocumentItem) => {
    setDownloadingId(doc.id);
    try {
      const url = await downloadMutation.mutateAsync(doc.id);
      const link = document.createElement('a');
      link.href = url;
      link.download = doc.original_filename;
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      console.error('Failed to download document:', err);
      toast.error('Dokumentet kunne ikke åbnes.');
    } finally {
      setDownloadingId(null);
    }
  };

  return (
    <>
      <PortalPageHead title="Dokumenter" subtitle="Filer, som klinikken har delt med dig." />
      {isLoading ? (
        <section className="card">
          <p className="help">Henter dokumenter…</p>
        </section>
      ) : error ? (
        <section className="card">
          <p className="help">Dokumenterne kunne ikke hentes. Prøv igen.</p>
        </section>
      ) : documents.length === 0 ? (
        <section className="card">
          <p className="help">Klinikken har ikke delt dokumenter endnu.</p>
        </section>
      ) : (
        <section className="card">
          {documents.map((document) => {
            const ext = fileExt(document.original_filename, document.mime_type);
            return (
              <div key={document.id} className="doc">
                <span className={`ft ${ext === 'PDF' ? 'pdf' : document.mime_type.startsWith('image/') ? 'img' : 'doc'}`}>
                  {ext}
                </span>
                <div className="grow">
                  <div className="t">{document.original_filename}</div>
                  <div className="s">
                    {document.clinic_name} · {formatPortalShortDate(document.created_at)} ·{' '}
                    {formatPortalFileSize(document.size_bytes)}
                  </div>
                </div>
                <div className="r">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => void handleDownload(document)}
                    disabled={downloadingId === document.id}
                  >
                    <PortalIcon name="download" size="sm" />
                    {downloadingId === document.id ? 'Henter…' : 'Download'}
                  </button>
                </div>
              </div>
            );
          })}
        </section>
      )}
      <p className="help" style={{ marginTop: 16 }}>
        Vil du sende en fil til klinikken? Vedhæft den i en{' '}
        <Link to="/portal/messages" className="link">
          besked
        </Link>
        .
      </p>
    </>
  );
}

function fileExt(filename: string, mime: string): string {
  const fromName = filename.split('.').pop()?.toUpperCase();
  if (fromName && fromName.length <= 4) return fromName;
  if (mime === 'application/pdf') return 'PDF';
  if (mime.startsWith('image/')) return 'BIL';
  return 'FIL';
}
